import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for reasonable time
      gcTime: 30 * 60 * 1000, // 30 minutes garbage collection (keep cached data longer)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && error.message.includes('4')) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      refetchOnWindowFocus: false, // Disable focus refetching to avoid unnecessary requests
      refetchOnMount: true, // Default to loading data when components mount
      refetchOnReconnect: 'always', // Refresh data when connection restored
      refetchInterval: false, // No automatic polling by default
      refetchIntervalInBackground: false, // Disable background intervals
    },
    mutations: {
      // Global mutation options
      retry: 1,
      retryDelay: 1000,
      // Mutations don't need network retry by default
      networkMode: 'online',
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

export { queryClient };
